import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { ORDER_URL } from "../data/site";
import { SectionTitle } from "./PopularCombos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generic fallback by category (used when an item has no image OR the URL 404s).
// Keys MUST match the actual category ids in menu_data.json.
const FALLBACK_BY_CATEGORY = {
  combos: "/combos/btw-super-combo.jpg",
  burgers: "/combos/btw-slider-combo.jpg",
  "taco-bar": "/combos/btw-3-tacos-combo.jpg",
  wings: "/catering/btw-20-wing-sampler.jpg",
  seafood: "/combos/btw-5-fries.jpg",
  "sides-soups-salads": "/combos/btw-5-fries.jpg",
  drinks: "/hero/btw-spread.jpg",
  dessert: "/catering/carrot-cake-14-slices-8b3dea42.jpg",
  "family-meals": "/combos/btw-super-combo.jpg",
  specials: "/combos/btw-3-tacos-combo.jpg",
};
const GLOBAL_FALLBACK = "/hero/btw-spread.jpg";

function resolveImg(item, categoryId) {
  const raw = (item?.img || "").trim();
  if (raw) return raw;
  return FALLBACK_BY_CATEGORY[categoryId] || GLOBAL_FALLBACK;
}

export default function MenuPreview() {
  const [menu, setMenu] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/menu`);
        if (!alive) return;
        setMenu(data);
        setActive(data.categories?.[0]?.id ?? null);
      } catch (e) {
        console.error("menu fetch failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const activeCategory = menu?.categories?.find((c) => c.id === active);
  const items = activeCategory?.items ?? [];

  return (
    <section
      id="menu"
      data-testid="menu-preview-section"
      className="py-12 sm:py-24 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #16181c 0%, #1c1f24 50%, #16181c 100%)",
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 400px at 50% 100%, rgba(163,217,0,0.06), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionTitle title="OUR MENU" dark />
        <p className="mt-4 text-center text-xs sm:text-sm tracking-[0.3em] uppercase text-white/45">
          Live menu · In-store pricing
        </p>

        {loading && (
          <div className="mt-12 flex justify-center">
            <Loader2 className="animate-spin text-white/60" size={24} />
          </div>
        )}

        {!loading && menu && (
          <>
            {/* Tabs — 5×2 grid on mobile (no scroll), wrap-flex on desktop */}
            <div className="mt-10 grid grid-cols-5 gap-1.5 sm:flex sm:flex-wrap sm:justify-center sm:gap-3 pb-2">
              {menu.categories.map((tab) => {
                const isActive = tab.id === active;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActive(tab.id)}
                    data-testid={`menu-tab-${tab.id}`}
                    className={`rounded-full font-extrabold uppercase tracking-[0.1em] sm:tracking-[0.18em] text-[9px] sm:text-xs leading-tight px-1 sm:px-6 py-2 sm:py-3 border-2 transition-all ${
                      isActive
                        ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]"
                        : "bg-white/5 text-white/80 border-white/15 hover:border-[#5FBDD6] hover:text-white"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Items grid — justify-items-start + lg:max-w-[300px] keeps
                partial rows looking natural instead of stretching the lone
                card on a row to 1/N of a 1400px container. */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 justify-items-start">
              {items.map((item) => (
                <article
                  key={item.name}
                  data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="bg-white/95 backdrop-blur-sm rounded-2xl border border-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.3)] hover:shadow-[0_14px_36px_rgba(0,0,0,0.45)] hover:-translate-y-1 transition-all overflow-hidden flex flex-col w-full lg:max-w-[300px]"
                >
                  <div className="relative aspect-square overflow-hidden bg-[#0B0B0B]">
                    <img
                      src={resolveImg(item, active)}
                      alt={item.name}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          FALLBACK_BY_CATEGORY[active] || GLOBAL_FALLBACK;
                      }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  <div className="px-3 pt-3 pb-4 text-center flex flex-col flex-1">
                    <h3 className="font-display text-base sm:text-lg uppercase tracking-tight text-[#0B0B0B] leading-tight">
                      {item.name}
                    </h3>
                    {item.desc && (
                      <p className="mt-1 text-[10px] sm:text-[11px] text-black/55 leading-snug line-clamp-2">
                        {item.desc}
                      </p>
                    )}
                    <div
                      className="font-display text-2xl mt-auto pt-2"
                      style={{ color: "#5FBDD6" }}
                    >
                      {item.price}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        <div className="mt-12 flex justify-center">
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-order-now-btn"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#5FBDD6] text-[#5FBDD6] hover:bg-[#5FBDD6] hover:text-white font-extrabold tracking-[0.18em] uppercase px-8 py-3 text-xs transition-colors"
          >
            Order Now
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
